// Yoj wishlist: products a visitor saves with a heart, kept in their own browser (no account, no app), and the page
// that shows them. Hearts sit on the theme's product cards (snippets/yoj-wishlist-heart.liquid, in the card's picture)
// and on the product page (blocks/yoj-wishlist-save.liquid); the header shows how many are saved
// (snippets/yoj-wishlist-header.liquid). The wishlist page (sections/yoj-wishlist.liquid) asks the search page for its
// own section with exactly the saved products (q=id:1 OR id:2 ..., sold-out ones included), which renders them as the
// theme's own product cards; a product the search can't find is asked for at its own address. A shared link carries
// the list itself (?shared=id~handle,...), so it opens on another device, or for someone else.
// Each saved product is { id, handle, t }: the handle reaches a product the search can't find, t is when it was saved.
// An app that takes over later can read the same key to bring each visitor's list across.

export const KEY = 'yoj-wishlist';
export const LIMIT = 100;
export const CHANGED = 'yoj:wishlist-changed';
// Products to a search; a longer list takes more than one
export const CHUNK = 25;

const isId = (id) => /^\d{1,20}$/.test(id);
// Shopify handles are letters (any alphabet), digits and hyphens: never spaces or the characters a shared link uses
const isHandle = (handle) => typeof handle === 'string' && /^[^\s/?#~,%]{1,255}$/u.test(handle);

// The saved list, newest first. Anything malformed or repeated is dropped, so a damaged entry can never break a page.
export function readList(storage) {
  let items;
  try {
    items = JSON.parse(storage.getItem(KEY) ?? 'null')?.items;
  } catch {
    return [];
  }
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  const list = [];
  for (const item of items) {
    const id = String(item?.id ?? '');
    if (!isId(id) || seen.has(id) || !isHandle(item.handle)) continue;
    seen.add(id);
    list.push({ id, handle: item.handle, t: Number.isFinite(item.t) ? item.t : 0 });
    if (list.length === LIMIT) break;
  }
  return list;
}

export function writeList(storage, list) {
  const kept = list.slice(0, LIMIT);
  storage.setItem(KEY, JSON.stringify({ v: 1, items: kept }));
  return kept;
}

// Saving puts the product first. A full list refuses rather than quietly dropping the oldest.
export function save(storage, product, now) {
  const list = readList(storage);
  if (list.some((item) => item.id === product.id)) return { list, saved: true };
  if (list.length >= LIMIT) return { list, saved: false, full: true };
  return { list: writeList(storage, [{ id: product.id, handle: product.handle, t: now }, ...list]), saved: true };
}

// Says where it was, so Undo can put it back there
export function unsave(storage, id) {
  const list = readList(storage);
  const index = list.findIndex((item) => item.id === id);
  if (index < 0) return { list, index, item: null };
  return { list: writeList(storage, list.filter((item) => item.id !== id)), index, item: list[index] };
}

// Undo: back where it was, unless the list has filled up meanwhile
export function restore(storage, item, index) {
  const list = readList(storage).filter((saved) => saved.id !== item.id);
  if (list.length >= LIMIT) return list;
  list.splice(Math.max(0, Math.min(index, list.length)), 0, item);
  return writeList(storage, list);
}

// A shared list's "Save all": the products not saved yet go first, in the shared order, as far as the limit allows
export function saveAll(storage, items, now) {
  const list = readList(storage);
  const have = new Set(list.map((item) => item.id));
  const fresh = items
    .filter((item) => !have.has(item.id))
    .slice(0, Math.max(0, LIMIT - list.length))
    .map((item, index) => ({ id: item.id, handle: item.handle, t: now - index }));
  if (fresh.length) writeList(storage, [...fresh, ...list]);
  return fresh.length;
}

// A shared link's list: id~handle,id~handle
export const shareText = (list) => list.map((item) => `${item.id}~${item.handle}`).join(',');

export function readShare(text) {
  const seen = new Set();
  const list = [];
  for (const part of String(text ?? '').split(',')) {
    const [id, handle, extra] = part.split('~');
    if (extra !== undefined || !isId(id) || !isHandle(handle) || seen.has(id)) continue;
    seen.add(id);
    list.push({ id, handle, t: 0 });
    if (list.length === LIMIT) break;
  }
  return list;
}

export function chunks(list, size) {
  const out = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
}

// Puts [count] and [title] into a message
export const fill = (text, values) => String(text ?? '').replace(/\[(count|title)\]/g, (match, name) => String(values[name] ?? match));

// The saved products as the section's cards: { found: Map(id -> element), gone: [ids], failed: count }. Searches
// first (sold-out products included), then each product the searches missed at its own address; only a product whose
// own address says "not found" counts as gone, so a moment's network trouble never empties a list.
export async function fetchCards(list, sectionId, { fetch: get = fetch, searchUrl = '/search', root = '/', origin = location.origin } = {}) {
  const found = new Map();
  const gone = [];
  let failed = 0;
  const cardsIn = (html) => [...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('yoj-wishlist-cards[data-filled] [data-yoj-wl-id]')];

  await Promise.all(chunks(list, CHUNK).map(async (part) => {
    try {
      const url = new URL(searchUrl, origin);
      url.searchParams.set('q', part.map((item) => `id:${item.id}`).join(' OR '));
      url.searchParams.set('type', 'product');
      url.searchParams.set('options[unavailable_products]', 'show');
      url.searchParams.set('section_id', sectionId);
      const response = await get(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      for (const card of cardsIn(await response.text())) found.set(card.dataset.yojWlId, card);
    } catch {}
  }));

  const one = async (item) => {
    try {
      const address = `${root}products/${encodeURIComponent(item.handle)}`;
      const page = new URL(address, origin);
      page.searchParams.set('section_id', sectionId);
      const response = await get(page, { headers: { Accept: 'text/html' } });
      const card = response.ok ? cardsIn(await response.text()).find((element) => element.dataset.yojWlId === item.id) : null;
      if (card) return void found.set(item.id, card);
      const data = await get(new URL(`${address}.js`, origin), { headers: { Accept: 'application/json' } });
      if (data.status === 404) gone.push(item.id);
      else failed++;
    } catch {
      failed++;
    }
  };
  // The few the searches missed, a few at a time
  for (const part of chunks(list.filter((item) => !found.has(item.id)), 4)) await Promise.all(part.map(one));
  return { found, gone, failed };
}

// Browsers can refuse storage (blocked site data, some private windows): then hearts say so instead of pretending
export function localStore() {
  try {
    const storage = window.localStorage;
    storage.getItem(KEY);
    return storage;
  } catch {
    return null;
  }
}

const readStored = () => {
  const storage = localStore();
  return storage ? readList(storage) : [];
};

// The shop's words and the wishlist page's address, from snippets/yoj-wishlist-setup.liquid
const DEFAULTS = {
  url: '',
  view: 'View wishlist',
  saved: 'Saved to your wishlist',
  removed: 'Removed from your wishlist',
  full: 'Your wishlist is full. Remove a product to save another.',
  blocked: "Your browser isn't letting this site save anything, so the wishlist can't work here.",
};
let config;
function words() {
  if (config) return config;
  try {
    const given = JSON.parse(document.getElementById('yoj-wishlist-config')?.textContent ?? '{}');
    // An empty setting keeps the default words
    config = { ...DEFAULTS, ...Object.fromEntries(Object.entries(given).filter(([, value]) => typeof value === 'string' && value.trim())) };
  } catch {
    config = { ...DEFAULTS };
  }
  return config;
}

// detail: { list } always (the saved list after the change), and { id, saved, index, item, from } for one product,
// { page } for the wishlist page's own changes, { external } for another tab or a page back from the browser's memory
const changed = (detail) => document.dispatchEvent(new CustomEvent(CHANGED, { detail }));

// One message area for the whole page, made at the start so screen readers read out what it later says; shown for a
// few seconds, longer while the pointer or keyboard is on it
let toast;
let toastTimer;
function makeToast() {
  if (toast || !document.body) return;
  toast = document.createElement('div');
  toast.className = 'yoj-wl-toast';
  toast.setAttribute('role', 'status');
  const text = document.createElement('p');
  text.className = 'yoj-wl-toast__text';
  const link = document.createElement('a');
  link.className = 'yoj-wl-toast__link';
  link.hidden = true;
  toast.append(text, link);
  const later = () => {
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('yoj-wl-toast--shown'), 5000);
  };
  toast.addEventListener('pointerenter', () => clearTimeout(toastTimer));
  toast.addEventListener('focusin', () => clearTimeout(toastTimer));
  toast.addEventListener('pointerleave', later);
  toast.addEventListener('focusout', later);
  toast.later = later;
  document.body.append(toast);
}

function tell(text, { link = false } = {}) {
  makeToast();
  if (!toast) return;
  const { url, view } = words();
  toast.querySelector('.yoj-wl-toast__text').textContent = text;
  const anchor = toast.querySelector('.yoj-wl-toast__link');
  anchor.hidden = !(link && url);
  if (!anchor.hidden) {
    anchor.href = url;
    anchor.textContent = view;
  }
  toast.classList.add('yoj-wl-toast--shown');
  toast.later();
}

// The heart on a card, and the product page's Save button
function defineButton() {
  if (customElements.get('yoj-wishlist-button')) return;

  class YojWishlistButton extends HTMLElement {
    connectedCallback() {
      this.sync ??= (event) => this.show((event?.detail?.list ?? readStored()).some((item) => item.id === this.dataset.productId));
      document.addEventListener(CHANGED, this.sync);
      if (!this.button) {
        const button = this.querySelector('button');
        if (!button || !isId(this.dataset.productId) || !isHandle(this.dataset.handle)) return;
        this.button = button;
        button.addEventListener('click', (event) => {
          // A click on the heart isn't a click on the card around it
          event.preventDefault();
          event.stopPropagation();
          this.toggle();
        });
      }
      this.sync();
      this.toggleAttribute('data-ready', true);
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGED, this.sync);
    }

    show(saved) {
      if (!this.button) return;
      this.toggleAttribute('data-saved', saved);
      if (this.dataset.style === 'card') {
        this.button.setAttribute('aria-label', saved ? this.dataset.labelRemove : this.dataset.labelSave);
        return;
      }
      const text = this.querySelector('[data-ref="text"]');
      if (text) text.textContent = saved ? this.dataset.textSaved : this.dataset.textSave;
      this.closest('.yoj-wl-save')?.querySelector('[data-ref="view"]')?.toggleAttribute('hidden', !saved);
    }

    toggle() {
      const { blocked, full, saved, removed } = words();
      const storage = localStore();
      if (!storage) return tell(blocked);
      const id = this.dataset.productId;
      const page = this.closest('yoj-wishlist');
      // On the visitor's own wishlist page, the page itself says what happened (with Undo)
      const ownPage = page && !page.hasAttribute('data-shared');
      try {
        if (readList(storage).some((item) => item.id === id)) {
          const { list, index, item } = unsave(storage, id);
          changed({ list, id, saved: false, index, item, from: this });
          if (!ownPage) tell(removed);
        } else {
          const result = save(storage, { id, handle: this.dataset.handle }, Date.now());
          if (result.full) return tell(full);
          changed({ list: result.list, id, saved: true, from: this });
          // A small beat as it fills
          this.toggleAttribute('data-beat', true);
          setTimeout(() => this.removeAttribute('data-beat'), 400);
          tell(saved, { link: !ownPage });
        }
      } catch {
        tell(blocked);
      }
    }
  }

  customElements.define('yoj-wishlist-button', YojWishlistButton);
}

// The header's count
function defineCount() {
  if (customElements.get('yoj-wishlist-count')) return;

  class YojWishlistCount extends HTMLElement {
    connectedCallback() {
      this.sync ??= (event) => this.show((event?.detail?.list ?? readStored()).length);
      document.addEventListener(CHANGED, this.sync);
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGED, this.sync);
    }

    show(count) {
      this.textContent = count > 99 ? '99+' : String(count);
      this.hidden = count === 0;
      const link = this.closest('a');
      if (link?.dataset.label) link.setAttribute('aria-label', count ? fill(link.dataset.labelCount, { count }) : link.dataset.label);
    }
  }

  customElements.define('yoj-wishlist-count', YojWishlistCount);
}

// The wishlist page
function definePage() {
  if (customElements.get('yoj-wishlist')) return;

  class YojWishlist extends HTMLElement {
    connectedCallback() {
      if (this.started || 'sample' in this.dataset) return;
      this.started = true;
      this.ref = (name) => this.querySelector(`[data-ref="${name}"]`);
      this.grid = this.ref('grid')?.querySelector('.resource-list');
      const shared = new URLSearchParams(location.search).get('shared');
      this.shared = shared === null ? null : readShare(shared);
      this.toggleAttribute('data-shared', this.shared !== null);
      this.ref('share')?.addEventListener('click', () => this.share());
      this.ref('clear')?.addEventListener('click', () => this.confirmClear(true));
      this.ref('confirm-yes')?.addEventListener('click', () => this.clear());
      this.ref('confirm-no')?.addEventListener('click', () => this.confirmClear(false));
      this.ref('save-all')?.addEventListener('click', () => this.saveAll());
      this.onChange = (event) => this.changed(event.detail ?? {});
      document.addEventListener(CHANGED, this.onChange);
      this.load();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGED, this.onChange);
    }

    // loading, empty, blocked or list
    state(name) {
      this.dataset.state = name;
      for (const part of ['loading', 'empty', 'blocked']) {
        const element = this.ref(part);
        if (element) element.hidden = part !== name;
      }
      const tools = this.ref('tools');
      if (tools) tools.hidden = name !== 'list';
      if (name !== 'list') this.confirmClear(false, false);
    }

    async load() {
      const ticket = (this.ticket = (this.ticket ?? 0) + 1);
      const storage = localStore();
      if (this.shared === null && !storage) return this.state('blocked');
      const list = this.shared ?? readList(storage);
      if (!list.length) return this.show([], ticket);
      if (!this.shownItems) this.state('loading');
      const { found, gone, failed } = await fetchCards(list, this.dataset.sectionId, {
        searchUrl: window.Theme?.routes?.search_url ?? `${window.Shopify?.routes?.root ?? '/'}search`,
        root: window.Shopify?.routes?.root ?? '/',
      });
      if (ticket !== this.ticket) return;
      // Products the shop no longer has come off the visitor's own list, and the page says so
      if (this.shared === null && gone.length) {
        try {
          const kept = writeList(storage, readList(storage).filter((item) => !gone.includes(item.id)));
          changed({ list: kept, page: this });
        } catch {}
      }
      this.notes(this.shared === null ? gone.length : 0, failed);
      this.show(list.filter((item) => found.has(item.id)).map((item) => ({ ...item, card: found.get(item.id) })), ticket);
    }

    notes(gone, failed) {
      const note = this.ref('note');
      if (!note) return;
      const parts = [];
      if (gone) parts.push(fill(gone === 1 ? this.dataset.goneOne : this.dataset.goneOther, { count: gone }));
      if (failed) parts.push(this.dataset.failed);
      note.textContent = parts.join(' ');
      note.hidden = parts.length === 0;
    }

    show(items, ticket) {
      if (ticket !== this.ticket || !this.grid) return;
      this.shownItems = items.map((item) => ({ ...item, node: document.importNode(item.card, true) }));
      this.grid.replaceChildren(...this.shownItems.map((item) => item.node));
      this.count();
      this.state(items.length ? 'list' : 'empty');
    }

    count() {
      const count = this.shownItems?.length ?? 0;
      const element = this.ref('count');
      if (element) element.textContent = count ? fill(count === 1 ? this.dataset.countOne : this.dataset.countOther, { count }) : '';
    }

    changed(detail) {
      // The page's own Remove all, Undo and Save all; a shared list doesn't follow the visitor's own
      if (detail.page === this || this.shared !== null) return;
      // Another tab, or this page back from the browser's memory
      if (detail.external) return void this.load();
      const index = this.shownItems?.findIndex((item) => item.id === detail.id) ?? -1;
      if (detail.saved) {
        // Saved with a heart elsewhere on this page, such as a recently viewed row
        if (index < 0 && detail.id) this.load();
        return;
      }
      if (index < 0) return;
      const [item] = this.shownItems.splice(index, 1);
      const hadFocus = item.node.contains(document.activeElement);
      item.node.remove();
      this.count();
      if (!this.shownItems.length) this.state('empty');
      this.offerUndo(fill(this.dataset.removed, { title: item.node.dataset.yojWlTitle ?? '' }), () => {
        const storage = localStore();
        if (!storage || !detail.item) return;
        try {
          const list = restore(storage, detail.item, detail.index);
          const at = Math.min(index, this.shownItems.length);
          this.grid.insertBefore(item.node, this.shownItems[at]?.node ?? null);
          this.shownItems.splice(at, 0, item);
          this.count();
          this.state('list');
          changed({ list, id: item.id, saved: true, page: this });
          item.node.querySelector('yoj-wishlist-button button')?.focus();
        } catch {}
      }, hadFocus || detail.from?.isConnected === false);
    }

    // A message with Undo. The keyboard moves to Undo when what it was on has just gone.
    offerUndo(text, undo, moveFocus) {
      const status = this.ref('status');
      if (!status) return;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button-unstyled yoj-wl__undo';
      button.textContent = this.dataset.undo;
      button.addEventListener('click', () => {
        status.replaceChildren();
        undo();
      });
      status.replaceChildren(`${text} `, button);
      if (moveFocus) button.focus();
    }

    say(text) {
      this.ref('status')?.replaceChildren(text);
    }

    confirmClear(open, moveFocus = true) {
      const confirm = this.ref('confirm');
      if (!confirm || confirm.hidden === !open) return;
      confirm.hidden = !open;
      if (open) {
        this.ref('confirm-text').textContent = fill(this.dataset.confirm, { count: this.shownItems?.length ?? 0 });
        this.ref('confirm-no')?.focus();
      } else if (moveFocus) {
        this.ref('clear')?.focus();
      }
    }

    clear() {
      const storage = localStore();
      if (!storage) return;
      const before = readList(storage);
      const shown = this.shownItems ?? [];
      try {
        writeList(storage, []);
      } catch {
        return;
      }
      this.shownItems = [];
      this.grid.replaceChildren();
      this.count();
      this.state('empty');
      changed({ list: [], page: this });
      this.offerUndo(fill(this.dataset.clearedAll, { count: shown.length }), () => {
        try {
          const list = writeList(storage, before);
          this.shownItems = shown;
          this.grid.replaceChildren(...shown.map((item) => item.node));
          this.count();
          this.state('list');
          changed({ list, page: this });
          this.ref('clear')?.focus();
        } catch {}
      }, true);
    }

    // The visitor's list as a link: the phone's share sheet where there is one, otherwise copied
    async share() {
      const url = new URL(location.pathname, location.origin);
      if ('needsView' in this.dataset) url.searchParams.set('view', 'wishlist');
      // The list as it reads (~ and , need no escaping in an address); a handle in another alphabet is escaped
      const list = (this.shownItems ?? []).map((item) => ({ id: item.id, handle: encodeURIComponent(item.handle) }));
      const link = `${url.href}${url.search ? '&' : '?'}shared=${shareText(list)}`;
      if (navigator.share && matchMedia('(pointer: coarse)').matches) {
        try {
          await navigator.share({ title: document.title, url: link });
          return;
        } catch (error) {
          if (error?.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(link);
        this.say(this.dataset.copied);
      } catch {
        // No clipboard here: the link, selected, to copy by hand
        const field = this.ref('link');
        if (!field) return;
        field.value = link;
        field.hidden = false;
        field.focus();
        field.select();
        this.say(this.dataset.copyByHand);
      }
    }

    saveAll() {
      const storage = localStore();
      if (!storage) return this.say(words().blocked);
      try {
        // Only those the shop still has: the ones on the page
        const added = saveAll(storage, (this.shownItems ?? []).map(({ id, handle }) => ({ id, handle })), Date.now());
        changed({ list: readList(storage), page: this });
        this.say(added ? fill(added === 1 ? this.dataset.savedAllOne : this.dataset.savedAllOther, { count: added }) : this.dataset.savedNone);
      } catch {
        this.say(words().blocked);
      }
    }
  }

  customElements.define('yoj-wishlist', YojWishlist);
}

// Another tab's change, and a page back from the browser's memory: everything here follows the saved list again
function followChanges() {
  window.addEventListener('storage', (event) => {
    if (event.key === KEY || event.key === null) changed({ list: readStored(), external: true });
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) changed({ list: readStored(), external: true });
  });
}

if (typeof customElements !== 'undefined' && typeof document !== 'undefined') {
  defineButton();
  defineCount();
  definePage();
  followChanges();
  makeToast();
}
